import { useSelector } from 'react-redux';

import { createSlice } from '@reduxjs/toolkit';
import {
	IAllBanksData,
	IAppInfo,
	ICollectRequestDetails,
	IHomeModalVisibility,
	IMandateCollectRequest,
	ISimDetailsObject,
	ITransactionPayload,
	IUpiDetails,
	IUpiStep,
	IUserUPIBankList,
} from 'types';
import { getAppInfo } from 'utils/nativeSupport';

const INITIAL_STATE = () => ({
	loading: false,
	error: '',
	screenClass: '',
	HomeModalVisibility: {} as IHomeModalVisibility,
	upiStep: {} as IUpiStep,
	upiData: {} as IUpiDetails,
	appInfo: getAppInfo(),
	deviceSimList: {} as ISimDetailsObject[],
	userUPIBankList: {} as IUserUPIBankList,
	allBankList: {} as IAllBanksData,
	transactionPayload: {} as ITransactionPayload,
	selectedBankData: {} as any,
	onBoardingRedirection: {} as any,
	selectedSim: [] as any,
	mandateCreatedDetails: {} as any,
	pendingMandateCollectData: {} as IMandateCollectRequest,
	mandateCollectData: {} as ICollectRequestDetails,
	collectRedirection: '' as string,
	upiStatusLoading: false,
});

export const AppSlice = createSlice({
	name: 'App',
	initialState: INITIAL_STATE(),
	reducers: {
		SET_HOME_MODAL_VISIBILITY: (state, payload) => {
			state.HomeModalVisibility = payload.payload;
		},
		RESET_HOME_MODAL_VISIBILITY: (state) => {
			state.HomeModalVisibility = INITIAL_STATE().HomeModalVisibility;
		},
		SET_UPI_STEP: (state, payload) => {
			state.upiStep = payload.payload;
		},
		SET_UPI_DATA: (state, payload) => {
			state.upiData = payload.payload;
		},
		RESET_UPI_STEP: (state) => {
			state.upiStep = INITIAL_STATE().upiStep;
		},
		RESET_UPI_DATA: (state) => {
			state.upiData = INITIAL_STATE().upiData;
		},
		SET_APP_INFO: (state, { payload }) => {
			state.appInfo = { ...getAppInfo(), ...payload.payload, nativeAppInfoUpdated: payload.nativeAppInfoUpdated };
		},
		SET_DEVICE_SIM_LIST: (state, payload) => {
			state.deviceSimList = payload.payload;
		},
		SET_USER_UPI_BANK_LIST: (state, payload) => {
			state.userUPIBankList = payload.payload;
		},
		SET_ALL_BANK_LIST: (state, payload) => {
			state.allBankList = payload.payload;
		},

		RESET_ALL_BANK_LIST: (state) => {
			state.allBankList = INITIAL_STATE().allBankList;
		},
		SET_TRANSACTION_PAYLAOD: (state, payload) => {
			state.transactionPayload = payload.payload;
		},
		SET_SELECTED_BANK_DATA: (state, payload) => {
			state.selectedBankData = payload.payload;
		},
		SET_ONBOARDING_REDIRECTION_PAGE: (state, payload) => {
			state.onBoardingRedirection = payload.payload;
		},
		SET_SELECTED_SIM: (state, payload) => {
			state.selectedSim = payload.payload;
		},
		RESET_SELECTED_SIM: (state) => {
			state.selectedSim = INITIAL_STATE().selectedSim;
		},
		SET_MANDATE_CREATED_DETAILS: (state, payload) => {
			state.mandateCreatedDetails = payload.payload;
		},
		SET_PENDING_MANDATE_COLLECT_REQUEST: (state, payload) => {
			state.pendingMandateCollectData = payload.payload;
		},
		SET_MANDATE_COLLECT_DATA: (state, payload) => {
			state.mandateCollectData = payload.payload;
		},
		SET_COLLECT_REDIRECTION_PAGE: (state, payload) => {
			state.collectRedirection = payload.payload;
		},
		SET_UPI_STATUS_LOADING: (state, payload) => {
			state.upiStatusLoading = payload.payload;
		},
	},

	extraReducers: {},
});

export const {
	SET_HOME_MODAL_VISIBILITY,
	RESET_HOME_MODAL_VISIBILITY,
	SET_UPI_STEP,
	SET_UPI_DATA,
	RESET_UPI_STEP,
	RESET_UPI_DATA,
	SET_APP_INFO,
	SET_DEVICE_SIM_LIST,
	SET_USER_UPI_BANK_LIST,
	SET_ALL_BANK_LIST,
	SET_TRANSACTION_PAYLAOD,
	SET_SELECTED_BANK_DATA,
	SET_ONBOARDING_REDIRECTION_PAGE,
	SET_SELECTED_SIM,
	RESET_SELECTED_SIM,
	SET_MANDATE_CREATED_DETAILS,
	SET_PENDING_MANDATE_COLLECT_REQUEST,
	SET_MANDATE_COLLECT_DATA,
	SET_COLLECT_REDIRECTION_PAGE,
	SET_UPI_STATUS_LOADING,
} = AppSlice.actions;

const homeModalVisibilitySelector = (state: IAppStates): any => state[AppSlice.name].HomeModalVisibility;

export interface IAppStates {
	[AppSlice.name]: ReturnType<typeof AppSlice.reducer>;
}

export const useHomeModalVisibility = (): any => useSelector(homeModalVisibilitySelector);

const UpiStepSelector = (state: IAppStates): IUpiStep => state[AppSlice.name].upiStep;
export const useUpiStep = () => useSelector(UpiStepSelector);

const UpiDataSelector = (state: IAppStates): IUpiDetails => state[AppSlice.name].upiData;
export const useUpiData = () => useSelector(UpiDataSelector);

export const appInfoSelector = (state: IAppStates): IAppInfo => {
	return state[AppSlice.name]?.appInfo;
};

export const useAppInfo = (): IAppInfo => useSelector(appInfoSelector);

const DeviceSimListSelector = (state: IAppStates): any => state[AppSlice.name].deviceSimList;

export const useDeviceSimList = () => useSelector(DeviceSimListSelector);

export const userUpiBankListSelector = (state: IAppStates): IUserUPIBankList => state[AppSlice.name].userUPIBankList;

export const useUserUpiBankList = () => useSelector(userUpiBankListSelector);

export const AllBankListSelector = (state: IAppStates): IAllBanksData => {
	return state[AppSlice.name]?.allBankList;
};

export const useAllBankList = (): IAllBanksData => useSelector(AllBankListSelector);

export const transactionPaylaodSelector = (state: IAppStates): ITransactionPayload =>
	state[AppSlice.name]?.transactionPayload;

export const useTransactionPaylaod = () => useSelector(transactionPaylaodSelector);

export const selectedBankDataSelector = (state: IAppStates): ITransactionPayload =>
	state[AppSlice.name]?.selectedBankData;

export const useSelectedBankData = () => useSelector(selectedBankDataSelector);

export const onBoardingRedirectionSelector = (state: IAppStates): any => state[AppSlice.name]?.onBoardingRedirection;

export const useOnBoardingRedirection = () => useSelector(onBoardingRedirectionSelector);

export const selectedSimSelector = (state: IAppStates): any => {
	return state[AppSlice.name]?.selectedSim;
};
export const useSelectedSim = (): any => useSelector(selectedSimSelector);

export const mandateCreatedDetailsSelector = (state: IAppStates): any => {
	return state[AppSlice.name]?.mandateCreatedDetails;
};
export const useMandateCreated = (): any => useSelector(mandateCreatedDetailsSelector);

export const pendingMandateCollectRequestSelector = (state: IAppStates): any => {
	return state[AppSlice.name]?.pendingMandateCollectData;
};
export const usePendingMandateCollectRequestData = (): any => useSelector(pendingMandateCollectRequestSelector);

export const mandateCollectRequestSelector = (state: IAppStates): any => {
	return state[AppSlice.name]?.mandateCollectData;
};
export const useMandateCollectRequestData = (): any => useSelector(mandateCollectRequestSelector);

export const onCollectRedirectionSelector = (state: IAppStates): any => state[AppSlice.name]?.collectRedirection;
export const useCollectRedirection = () => useSelector(onCollectRedirectionSelector);

export const onUpiStatusLoading = (state: IAppStates): any => state[AppSlice.name]?.upiStatusLoading;
export const useUpiStatusLoading = () => useSelector(onUpiStatusLoading);

export { triggerGetPaymentModesApi, paymentModesSlice, usePaymentModes } from './paymentModes';
export { triggerUpdatePaymentModesApi, updatePaymentModesSlice, useUpdatedPaymentModes } from './updatePaymentModes';

export { triggerMandateApi, mandateSlice, useMandate } from './GetMandate';

export { trigeerGetPaymentStatusApi, paymentStatusSlice, usePaymentStatus } from './paymentStatus';
export { LanguageSlice, useSelectedLanguage, SET_SELECTED_LANGUAGE } from './LanguageSlice';
export { triggerApplyPostpeCashbackApi, applyPostpeCashbackSlice, useApplyPostpeCashback } from './applyPostpeCashback';
export { triggerGetCashbackDetailsApi, cashbackDetailsSlice, useCashbackDetails } from './getPostpeCashbackDetails';
export { triggerJuspayPaymentModesApi, JuspayPaymentModesSlice, useJuspayPaymentModes } from './GetJuspayPaymentModes';
export { customerSettlementAccount, SET_CUSTOMER_SETTLEMENT_ACCOUNT_DATA } from './GetCustomerSettlementAccount';
export { SettlementAccountSlice, SET_SETTLEMENT_ACCOUNT_DATA } from './GetSettlementAccount';

export {
	triggerUpdateJuspayCashbackApi,
	UpdateJuspayCashbackSlice,
	useUpdateJuspayCashback,
} from './updateJuspayCashback';
export {
	triggerJuspayWebPaymentModesApi,
	JuspayWebPaymentModesSlice,
	useJuspayWebPaymentModes,
} from './GetJuspayWebPaymentModes';
